import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import { of } from 'rxjs';
import { loadProjekteSucceeded, loadProjekteFailed, loadProjekteStart } from '../actions/getAllProjekte.actions';
import {ProjektService} from '../../projekt.service';
import {addProjektFailed, addProjektStart, addProjektSucceeded} from '../actions/projekt-add.actions';
import {deleteProjektFailed, deleteProjektStart, deleteProjektSucceeded} from '../actions/projekt-del.actions';
import {editProjektFailed, editProjektStart, editProjektSucceeded} from '../actions/projekt-edit.actions';
import {getFilteredProjektFailed, getFilteredProjektStart, getFilteredProjektSucceeded} from '../actions/getFilteredProjekt.actions';
import {loadNodesFailed, loadNodesStart, loadNodesSucceeded} from '../actions/getAllNodes.actions';
import {addProjektstufeFailed, addProjektstufeStart, addProjektstufeSucceeded} from '../actions/projektstufe-add.actions';
import {archiveProjektFailed, archiveProjektStart, archiveProjektSucceeded} from '../actions/projekt-archive.actions';
import {editProjektstufeFailed, editProjektstufeStart, editProjektstufeSucceeded} from '../actions/projektstufe-edit.actions';
import {
  dearchiveProjektFailed,
  dearchiveProjektStart,
  dearchiveProjektSucceeded
} from '../actions/projekt-dearchive.actions';
import {loadUserNodesFailed, loadUserNodesStart, loadUserNodesSucceeded} from "../actions/getUserNodes.actions";
import {pasteProjektFailed, pasteProjektStart, pasteProjektSucceeded} from "../actions/projekt-paste.actions";
import {loadCopyNodesFailed, loadCopyNodesStart, loadCopyNodesSucceeded} from "../actions/getCopyNodes.actions";

@Injectable()
export class ProjektEffects {
    /**
     * Loads the address list when dispatching the start action
     */
    public loadProjekte$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadProjekteStart),
            mergeMap(() =>
                this.projektService.getAllProjekte().pipe(
                    map((response) => loadProjekteSucceeded({ item: response.data})),
                    catchError((e) => of(loadProjekteFailed(e))),
                ),
            ),
        ),
    );

  public loadFilteredProjekte$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFilteredProjektStart),
      mergeMap((projekt) =>
        this.projektService.getFilteredProjekte(projekt.string, projekt.item).pipe(
          mergeMap((response) => of(getFilteredProjektSucceeded({ item: response.data}))),
          catchError((e) => of(getFilteredProjektFailed(e))),
        ),
      ),
    ),
  );

  public loadNodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadNodesStart),
      mergeMap((archive) =>
        this.projektService.getAllNodes(archive.archiv).pipe(
          mergeMap((response) => of(loadNodesSucceeded({ item: response.data}))),
          catchError((e) => of(loadNodesFailed(e))),
        ),
      ),
    ),
  );

  public loadCopyNodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCopyNodesStart),
      mergeMap((projekt) =>
        this.projektService.getCopyNodes(projekt.item).pipe(
          mergeMap((response) => of(loadCopyNodesSucceeded({ item: response.data}))),
          catchError((e) => of(loadCopyNodesFailed(e))),
        ),
      ),
    ),
  );

  public loadUserNodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserNodesStart),
      mergeMap((projekt) =>
        this.projektService.getUserNodes().pipe(
          mergeMap((response) => of(loadUserNodesSucceeded({ item: response.data}))),
          catchError((e) => of(loadUserNodesFailed(e))),
        ),
      ),
    ),
  );


  public addProjekte$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addProjektStart),
      mergeMap((projekt) =>
        this.projektService.addProjekt(projekt.item).pipe(
          mergeMap((response) => of(addProjektSucceeded({ item: response}), loadNodesStart({archiv: 0}))),
          catchError((e) => of(addProjektFailed(e))),
        ),
      ),
    ),
  );

  public pasteProjekte$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pasteProjektStart),
      mergeMap((projekt) =>
        this.projektService.pasteProjekt(projekt.item, projekt.copyNodes).pipe(
          mergeMap((response) => of(pasteProjektSucceeded({ item: response}), loadNodesStart({archiv: 0}))),
          catchError((e) => of(pasteProjektFailed(e))),
        ),
      ),
    ),
  );

  public addProjektstufe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addProjektstufeStart),
      mergeMap((projekt) =>
        this.projektService.addProjektstufe(projekt.item).pipe(
          mergeMap((response) => of(addProjektstufeSucceeded({ item: response}), loadNodesStart({archiv: 0}))),
          catchError((e) => of(addProjektstufeFailed(e))),
        ),
      ),
    ),
  );

  public deleteProjekt = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteProjektStart),
      mergeMap((projekt) =>
        this.projektService.deleteProjekt(projekt.item).pipe(
          mergeMap((response) => of(deleteProjektSucceeded(), loadNodesStart({archiv: 0}))),
          catchError((e) => of(deleteProjektFailed(e))),
        ),
      ),
    ),
  );

  public archiveProjekt = createEffect(() =>
    this.actions$.pipe(
      ofType(archiveProjektStart),
      mergeMap((projekt) =>
        this.projektService.archiveProjekt(projekt.item).pipe(
          mergeMap((response) => of(archiveProjektSucceeded(), loadNodesStart({archiv: 0}))),
          catchError((e) => of(archiveProjektFailed(e))),
        ),
      ),
    ),
  );

  public dearchiveProjekt = createEffect(() =>
    this.actions$.pipe(
      ofType(dearchiveProjektStart),
      mergeMap((projekt) =>
        this.projektService.dearchiveProjekt(projekt.item).pipe(
          mergeMap((response) => of(dearchiveProjektSucceeded(), loadNodesStart({archiv: 1}))),
          catchError((e) => of(dearchiveProjektFailed(e))),
        ),
      ),
    ),
  );

  public editProjekt = createEffect(() =>
    this.actions$.pipe(
      ofType(editProjektStart),
      mergeMap((projekt) =>
        this.projektService.editProjekt(projekt.item).pipe(
          mergeMap((response) => of(editProjektSucceeded({ item: response}), loadNodesStart({archiv: 0}))),
          catchError((e) => of(editProjektFailed(e))),
        ),
      ),
    ),
  );


  public editProjektstufe = createEffect(() =>
    this.actions$.pipe(
      ofType(editProjektstufeStart),
      mergeMap((projekt) =>
        this.projektService.editProjektstufe(projekt.item).pipe(
          mergeMap((response) => of(editProjektstufeSucceeded({ item: response}), loadNodesStart({archiv: 0}))),
          catchError((e) => of(editProjektstufeFailed(e))),
        ),
      ),
    ),
  );


    /**
     * Constructor of address effects
     *
     * @param actions$ - The action observable
     * @param addressesService - The address service
     */
    constructor(private actions$: Actions, private projektService: ProjektService) {}
}
